import React, { useState } from "react";
import Navbar from "../components/Navbar";
import { BUNDLED_JOBS } from "../utils/helpers";

const API = process.env.REACT_APP_API_URL ?? "";
const PICKUP_ADDR = process.env.REACT_APP_PICKUP_ADDRESS || "Don Gil Garcia Street, Capitol Site, Cebu City";

export default function TrackApplication() {
  const [appId, setAppId] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [requestMsg, setRequestMsg] = useState("");

  // Phase 2 states
  const [showPhase2Form, setShowPhase2Form] = useState(false);
  const [phase2Job, setPhase2Job] = useState("");
  const [phase2Pickup, setPhase2Pickup] = useState("");

  const [submitting, setSubmitting] = useState(false);

  const lookup = async () => {
    setError(""); setResult(null); setRequestMsg("");
    setShowPhase2Form(false);
    if (!appId.trim()) { setError("Enter an Endorsement ID"); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/track`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ applicationId: appId.trim().toUpperCase() })
      });
      const data = await res.json();
      if (!data.ok) setError("No request found with that ID.");
      else setResult(data.result);
    } catch { setError("Error searching. Try again."); }
    setLoading(false);
  };

  const refreshResult = async (id) => {
    const res = await fetch(`${API}/api/track`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ applicationId: id })
    });
    const data = await res.json();
    if (data.ok) setResult(data.result);
  };

  const submitPhase2 = async () => {
    if (!phase2Job) { setRequestMsg("Select a company for Phase 2."); return; }
    if (!phase2Pickup) { setRequestMsg("Select a preferred pickup date and time."); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/applicant/request-phase2`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ applicationId: result.applicationId, phase2Job, pickupDate: phase2Pickup })
      });
      const data = await res.json();
      if (data.ok) {
        setRequestMsg("Request submitted! The agency will notify you once your documents are ready to receive.");
        setShowPhase2Form(false);
        await refreshResult(result.applicationId);
      } else setRequestMsg("Error: " + data.error);
    } catch { setRequestMsg("Connection error. Try again."); }
    setSubmitting(false);
  };

  const canRequestPhase2 = result?.endorsementStatus === "claimed" && !result?.phase2Status;

  return (
    <>
      <Navbar />
      <div className="page">
        <div className="container-narrow">
          <div className="page-header">
            <h1 className="page-title">Track Your Endorsement</h1>
            <p className="page-subtitle">Enter your Endorsement ID to check your document status request.</p>
          </div>

          {/* Search */}
          <div className="card">
            <div className="form-group">
              <label className="form-label">Endorsement ID</label>
              <div style={{ display: "flex", gap: 8 }}>
                <input className="form-control mono" placeholder="GGW-2026-XXXXXX" value={appId}
                  onChange={(e) => setAppId(e.target.value)} onKeyDown={(e) => e.key === "Enter" && lookup()} />
                <button className="btn btn-primary" onClick={lookup} disabled={loading}>
                  {loading ? <span className="spinner"></span> : <><i className="ti ti-search"></i> Track</>}
                </button>
              </div>
            </div>
            {error && <div className="alert alert-error">{error}</div>}
          </div>

          {/* Result */}
          {result && (
            <div className="card mt-24">

              {/* Header */}
              <div className="flex-between mb-16" style={{ flexWrap: "wrap", gap: 12 }}>
                <div>
                  <div className="text-muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em" }}>Endorsement ID</div>
                  <div className="mono" style={{ fontWeight: 700, fontSize: 20 }}>{result.applicationId}</div>
                  <div style={{ fontSize: 14, marginTop: 2 }}><strong>{result.firstName} {result.lastName}</strong></div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
                  {result.endorsementStatus && (
                    <span className={`badge badge-${result.endorsementStatus === "ready_to_claim" ? "ready" : result.endorsementStatus === "claimed" ? "claimed" : "processing"}`}>
                      {result.endorsementStatus.replace(/_/g, " ")}
                    </span>
                  )}
                </div>
              </div>

              {/* Status alerts */}
              {result.endorsementStatus === "processing" && <div className="alert alert-info mb-16">The agency is processing your Endorsement. Please wait for SMS or email notification when your documents are ready to receive.</div>}
              {result.endorsementStatus === "ready_to_claim" && <div className="alert alert-success mb-16">Your endorsement documents are ready. Contact your endorser to arrange the pickup date and time. Payment (₱6,000) is collected in cash when you receive your documents.</div>}
              {result.endorsementStatus === "claimed" && !result.phase2Status && (
                <div className="alert alert-success mb-16">
                  Your endorsement documents have been claimed. Please submit them to Golden Gateway Agency Office at {PICKUP_ADDR} for slot availability. The agency will guide you.
                </div>
              )}
              {result.phase2Status === "processing" && <div className="alert alert-info mb-16">Company Profile &amp; Business Documents are being processed. We will notify you when ready to receive.</div>}
              {result.phase2Status === "ready_to_claim" && <div className="alert alert-success mb-16">Your Company Profile &amp; Business Documents are ready. Contact your endorser to arrange pickup.</div>}
              {result.phase2Status === "claimed" && <div className="alert alert-success mb-16">All documents claimed. Thank you and good luck with your career!</div>}

              {requestMsg && <div className="alert alert-info mb-16">{requestMsg}</div>}

              {/* Timeline */}
              <h3 style={{ fontSize: 16, marginBottom: 12 }}>Status Timeline</h3>
              <div className="timeline" style={{ marginBottom: 20 }}>
                {(result.timeline || []).map((t, i) => (
                  <div key={i} className={`timeline-item ${t.done ? "done" : "active"}`}>
                    <div className="timeline-dot"><i className={`ti ${t.done ? "ti-check" : "ti-clock"}`}></i></div>
                    <div className="timeline-title">{t.label}</div>
                    <div className="timeline-meta">{t.date ? new Date(t.date).toLocaleString() : ""}</div>
                    {t.meta?.jobs && <div className="timeline-content" style={{ fontSize: 13, color: "var(--ink-500)" }}>Jobs: {t.meta.jobs.join(", ")}</div>}
                  </div>
                ))}

                {/* Phase 2 — Request Company Docs button in timeline */}
                {canRequestPhase2 && !showPhase2Form && (
                  <div className="timeline-item active">
                    <div className="timeline-dot" style={{ background: "var(--success)" }}><i className="ti ti-building"></i></div>
                    <div>
                      <div className="timeline-title">Endorsement Complete — Request Company Documents</div>
                      <div className="timeline-meta mb-8">You may now request Company Profile, Brochure &amp; Business Documents. Fee: PHP 6,000, payable upon receiving these documents.</div>
                      <button className="btn btn-success" style={{ fontSize: 13 }} onClick={() => setShowPhase2Form(true)}>
                        <i className="ti ti-file-plus"></i> Request Company Profile &amp; Business Documents
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Phase 2 Form */}
              {canRequestPhase2 && showPhase2Form && (
                <div style={{ padding: 20, background: "var(--cream-warm)", borderRadius: 10, border: "1px solid var(--ink-200)", marginBottom: 16 }}>
                  <div className="flex-between mb-16">
                    <h3 style={{ fontSize: 16, margin: 0 }}><i className="ti ti-building"></i> Company Profile, Brochure &amp; Business Documents</h3>
                    <button className="btn btn-outline" style={{ fontSize: 12 }} onClick={() => setShowPhase2Form(false)}><i className="ti ti-x"></i></button>
                  </div>
                  <p className="text-muted mb-16" style={{ fontSize: 13 }}>Select which company's documents you need. Fee: <strong>PHP 6,000</strong></p>
                  <div className="form-group">
                    <label className="form-label">Select Company</label>
                    <div style={{ display: "grid", gap: 8, gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))" }}>
                      {(result.selectedJobs?.length > 0 ? result.selectedJobs : BUNDLED_JOBS).map((job) => (
                        <label key={job} style={{ display: "flex", alignItems: "center", gap: 8, padding: 12, border: `2px solid ${phase2Job === job ? "var(--gold-500)" : "var(--ink-200)"}`, borderRadius: 8, background: phase2Job === job ? "var(--gold-50)" : "white", cursor: "pointer" }}>
                          <input type="radio" name="p2job" checked={phase2Job === job} onChange={() => setPhase2Job(job)} />
                          <span style={{ fontSize: 13 }}>{job}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="alert alert-info" style={{ fontSize: 13, marginBottom: 16 }}>
                    <i className="ti ti-info-circle"></i> Payment is collected in cash when you receive these documents — no GCash, no advance payment.
                  </div>
                  <div className="form-group">
                    <label className="form-label">Preferred Pickup Date &amp; Time *</label>
                    <input type="datetime-local" className="form-control" value={phase2Pickup} onChange={(e) => setPhase2Pickup(e.target.value)} />
                    <small className="text-muted">Pickup at: {PICKUP_ADDR}</small>
                  </div>
                  <button className="btn btn-success" onClick={submitPhase2} disabled={submitting || !phase2Job || !phase2Pickup}>
                    {submitting ? <span className="spinner"></span> : <><i className="ti ti-send"></i> Submit Request</>}
                  </button>
                </div>
              )}

            </div>
          )}
        </div>
      </div>
    </>
  );
}
